<template>
  <ontos-base v-bind="baseProps" ref="base">
    <ontos-page-header :title="workspace.title"/>
    <!-- A apresentação que o designer escreveu, quando há, sob o nome, linearizada: a de uma linha
         inteira; a de mais linhas em até duas, com o ⤢ que abre o texto desenhado por cima — o mesmo
         gesto da grade e da ficha (escolha do usuário). Ela inteira no fim da página saiu. O corte é
         o da largura, e não um teto em caracteres, que cortava no meio da palavra com espaço
         sobrando (relato do usuário). -->
    <ontos-markdown-expandable v-if="description" :value="description" full
                               class="workspace-description text-body-1"/>

    <!-- Em tela larga, as coleções e os alterados recentemente lado a lado; empilhados, abaixo disso.
         Uma coluna só deixava a linha de cada coleção esticada na tela inteira (relato do usuário). -->
    <div class="landing-columns">
    <!-- Uma linha por coleção (escolha do usuário entre cartões, lista e duas colunas): o nome, quantos
         itens, como se distribuem pelas situações, quando mudou por último e os atalhos. Lista, e não
         cartões, porque escala para muitas coleções sem virar um mosaico. -->
    <ontos-section :title="$tt('collections')" :divider="false" :level="2" dense>
      <div class="collection-list">
        <div v-for="row of rows" :key="row.idea.alias" class="collection-row">
          <router-link class="collection-name text-subtitle-1" :to="routeTo('collection', row.idea)">
            {{ row.idea.pluralName }}
          </router-link>
          <!-- Os números chegam DEPOIS do resto, com o resumo, e entram acendendo (`ontos-arrives`). A
               célula existe desde o começo, vazia — a linha é uma grade, e sem ela as colunas seguintes
               se deslocariam —, então o `key` pelo texto é o que a recria quando ele chega, e é na
               criação que a animação roda. -->
          <span :key="row.countText" class="collection-count ontos-arrives text-body-2 text-medium-emphasis">
            {{ row.countText }}
          </span>
          <!-- A distribuição pelas situações, nas cores do fluxo — as mesmas da raia do Kanban. Sem
               fluxo, nem o trilho: a coluna fica, vazia, e as linhas continuam alinhadas. -->
          <div class="state-bar" :class="{'state-bar--empty': !row.segments.length}" :title="row.statesText">
            <span v-for="segment of row.segments" :key="segment.key" class="ontos-arrives"
                  :class="accentClass(segment.color)" :style="{flexGrow: segment.count}"/>
          </div>
          <span :key="row.updatedText" class="collection-updated ontos-arrives text-caption text-medium-emphasis">
            {{ row.updatedText }}
          </span>
          <div class="collection-actions">
            <v-btn icon variant="text" size="small" :title="$tt('list')" :to="routeTo('list-instances', row.idea)">
              <v-icon>mdi-view-list</v-icon>
            </v-btn>
            <!-- Pelo mesmo portão do quadro: uma coleção com um atributo de seleção e sem fluxo nenhum
                 também tem Kanban, e é justamente ela quem mais ganha com ele. -->
            <v-btn v-if="hasKanban(row.idea)" icon variant="text" size="small" :title="$tt('kanban')"
                   :to="routeTo('kanban', row.idea)">
              <v-icon>mdi-view-dashboard-outline</v-icon>
            </v-btn>
            <!-- A janela do casco, como toda criação, e não a página de criar: criar não tira
                 ninguém da página do ambiente (ver o `requestCreation`) -->
            <v-btn v-if="canCreateIn(row.idea)" icon variant="text" size="small"
                   :title="$tt('instance-create', {collection: row.idea.name})"
                   @click="requestCreation(row.idea.alias)">
              <v-icon>mdi-plus</v-icon>
            </v-btn>
          </div>
        </div>
      </div>
    </ontos-section>

    <!-- O que mudou por último no ambiente inteiro. É aqui que, depois, entra o que é de quem olha —
         os itens atribuídos a ele, os criados por ele (pedido do usuário para o futuro). -->
    <!-- Também vem com o resumo, e também entra acendendo -->
    <ontos-section v-if="recent.length" class="ontos-arrives" :title="$tt('recently-changed')"
                   :divider="false" :level="2" dense>
      <div class="collection-list">
        <ontos-recent-row v-for="item of recent" :key="`${item.idea}.${item._instance}`" :item="item"
                          :place="ideaName(item.idea)"
                          :to="{name: 'view-instance', params: {...$route.params, idea: item.idea, instance: item._instance}}"/>
      </div>
    </ontos-section>
    </div>
  </ontos-base>
</template>
<script lang="ts">
import {Component, Watch, toNative} from 'vue-facing-decorator'
import WorkspaceRouteComponent from "@/components/shared/WorkspaceRouteComponent";
import Idea from "@/types/Idea";
import {shownDescription} from "@/utils/markdownMentions";
import {hasDimension} from "@/utils/kanbanDimensions";
import Section from "@/components/shared/Section.vue";
import RecentRow from "@/components/shared/RecentRow.vue";
import MarkdownExpandable from "@/components/shared/markdown/MarkdownExpandable.vue";
import WorkspaceSummary, {CollectionSummary, RecentItem} from "@/types/WorkspaceSummary";
import {accentClass} from "@/utils/colors";
import {relativeTime} from "@/utils/relativeTime";
import {lastCreated, requestCreation} from "@/utils/instanceCreation";

/** Uma linha da lista de coleções, pronta para o template. */
type CollectionRow = {
  idea: Idea
  countText: string
  segments: Array<{ key: string, color?: string, count: number }>
  statesText: string
  updatedText: string
}

/**
 * A página de entrada de um ambiente: o nome, a apresentação que o designer escreveu e, de cada
 * coleção, os números — quantos itens, por situação, a última alteração —, mais o que mudou por
 * último no ambiente inteiro.
 *
 * Os números vêm do resumo do servidor (`summary`); o nome, o fluxo e as cores, do ambiente que a
 * tela já tem. Sem o resumo (uma falha ao buscá-lo), a lista continua — só sem os números.
 */
@Component({components: {
  'ontos-section': Section, 'ontos-recent-row': RecentRow, 'ontos-markdown-expandable': MarkdownExpandable
}})
class System extends WorkspaceRouteComponent {
  //Expostos ao template: funções de módulo, que ele não enxerga
  accentClass = accentClass
  requestCreation = requestCreation

  /** A descrição como se mostra, com as referências desenhadas — ver `shownDescription`. */
  get description(): string {
    return shownDescription(this.workspace, this.workspace)
  }

  summary: WorkspaceSummary | null = null

  /** Nasceu um item, por qualquer porta: a contagem e os alterados recentemente mudaram. */
  get createdNotice() {
    return lastCreated.value
  }

  @Watch('createdNotice')
  async onCreatedNotice() {
    await this.load()
  }

  async load() {
    try {
      this.summary = await this.$systems.summary({systemAlias: this.params.system as string})
    } catch (err) {
      console.warn('Não foi possível carregar o resumo do ambiente', err)
    }
  }

  get rows(): CollectionRow[] {
    const collections = this.summary ? this.summary.collections : []
    return this.workspace.ideas.map(idea => this.rowOf(idea, collections.find(collection => collection.idea === idea.alias)))
  }

  get recent(): RecentItem[] {
    return this.summary ? this.summary.recent : []
  }

  /**
   * Os números de uma coleção como a linha os mostra. As situações na ordem do fluxo, e os itens sem
   * situação gravada contados na inicial — a mesma regra do resto do app (`currentStateOf`).
   */
  rowOf(idea: Idea, summary?: CollectionSummary): CollectionRow {
    const workflow = this.workspace.workflowOf(idea)
    const counts = new Map<number, number>()
    if (summary && workflow) for (const {state, count} of summary.states) {
      const id = workflow.currentStateOf({_state: state ?? undefined}).id
      counts.set(id, (counts.get(id) || 0) + count)
    }
    const states = workflow ? workflow.states.filter(state => counts.get(state.id)) : []
    return {
      idea,
      countText: summary ? this.$tc('item-count', summary.count, {count: summary.count}) : '',
      segments: states.map(state => ({key: String(state.id), color: workflow!.colorOfState(state.id), count: counts.get(state.id)!})),
      statesText: states.map(state => `${state.name}: ${counts.get(state.id)}`).join(' · '),
      updatedText: summary && summary.updatedAt ? relativeTime(summary.updatedAt) : ''
    }
  }

  ideaName(alias: string): string {
    const idea = this.workspace.ideaWith(alias)
    return idea ? idea.name : alias
  }

  routeTo(name: string, idea: Idea) {
    return {name, params: {...this.$route.params, idea: idea.alias}}
  }

  hasKanban(idea: Idea): boolean {
    return hasDimension(idea, this.workspace.ideas)
  }
}

export default toNative(System)
</script>

<style scoped>
.workspace-description {
  margin-bottom: 24px;
}

/* Empilhadas, com o vão que a seção daria entre si; lado a lado em tela larga. O vão é o `gap`, e
   não a margem da seção, que na segunda coluna a desalinharia da primeira */
/* A coluna única declarada `minmax(0, 1fr)`, pelo mesmo motivo da tela inicial (ver .home-columns):
   uma `auto` crescia até o texto inteiro do que corta com reticências, e passava da tela */
.landing-columns {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  row-gap: 28px;
  column-gap: 48px;
}

.landing-columns > .ontos-section {
  margin-top: 0;
}

@media (min-width: 1280px) {
  .landing-columns {
    grid-template-columns: minmax(0, 3fr) minmax(0, 2fr);
    align-items: start;
  }
}

/* As linhas como uma tabela leve: colunas que se alinham de uma linha para a outra, sem as bordas
   de tabela. Cada linha é uma grade própria, então só o nome tem largura flexível: com uma coluna
   `auto`, a linha de dois atalhos tinha a coluna mais estreita que a de três, e desalinhava as
   outras (achado no teste). A barra tem teto — esticada, era um trilho cinza do tamanho da tela. */
.collection-list {
  display: flex;
  flex-direction: column;
}

.collection-row {
  display: grid;
  grid-template-columns: minmax(8rem, 1fr) 6rem minmax(4rem, 10rem) 7rem 7rem;
  align-items: center;
  column-gap: 16px;
  padding: 6px 4px;
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.collection-name {
  font-weight: 500;
  color: inherit;
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.collection-name:hover {
  text-decoration: underline;
}

/* O trilho é a coluna inteira; os segmentos dividem o trilho na proporção de cada situação */
.state-bar {
  display: flex;
  height: 8px;
  border-radius: 4px;
  overflow: hidden;
  background-color: rgba(var(--v-theme-on-surface), 0.08);
}

.state-bar--empty {
  background-color: transparent;
}

.collection-actions {
  display: flex;
  justify-content: flex-end;
}

/* No telefone, cada coleção em três faixas: o nome e os atalhos; quantos e quando; a barra. Cada
   célula com o lugar dito, e não encaixada pela grade, que as embaralhava */
@media (max-width: 600px) {
  .collection-row {
    grid-template-columns: 1fr auto;
    row-gap: 4px;
  }

  .collection-name { grid-row: 1; grid-column: 1; }
  .collection-actions { grid-row: 1; grid-column: 2; }
  .collection-count { grid-row: 2; grid-column: 1; }
  .collection-updated { grid-row: 2; grid-column: 2; text-align: right; }
  .state-bar { grid-row: 3; grid-column: 1 / -1; }
}
</style>
